const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

export interface AuthTokens {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  id_token?: string;
}

export interface UserInfo {
  sub: string;
  email?: string;
  preferred_username?: string;
  name?: string;
}

// Storage keys
const STORAGE_KEYS = {
  ACCESS_TOKEN: 'admin_access_token',
  REFRESH_TOKEN: 'admin_refresh_token',
  EXPIRES_AT: 'admin_expires_at',
  ID_TOKEN: 'admin_id_token',
  USER_INFO: 'admin_user_info',
};

/**
 * Complete a custom social login (Google/Apple) by exchanging the provider's
 * ID token with the backend for our own admin session tokens, then persisting
 * them. The backend rejects the exchange (403) unless the user is an admin.
 */
async function completeSocialLogin(
  provider: 'google' | 'apple',
  body: Record<string, unknown>
): Promise<void> {
  const response = await fetch(`${API_URL}/api/admin/auth/social/${provider}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });

  const json = await response.json().catch(() => null);

  if (!response.ok || !json?.success) {
    throw new Error(json?.error?.message || 'Login failed. Please try again.');
  }

  const { access_token, refresh_token, expires_in } = json.data;
  storeTokens({ access_token, refresh_token, expires_in });

  const decoded = decodeToken(access_token);
  if (decoded) {
    storeUserInfo(decoded);
  }
}

/**
 * Log in with a Google ID token (credential from Google Identity Services).
 */
export async function loginWithGoogle(idToken: string): Promise<void> {
  await completeSocialLogin('google', { id_token: idToken });
}

/**
 * Log in with an Apple ID token. `user` is only present on the first
 * authorization and carries the user's name (Apple omits it from the token).
 */
export async function loginWithApple(
  idToken: string,
  user?: { name?: { firstName?: string; lastName?: string } }
): Promise<void> {
  await completeSocialLogin('apple', { id_token: idToken, user });
}

/**
 * Store authentication tokens
 */
export function storeTokens(tokens: AuthTokens): void {
  const expiresAt = Date.now() + tokens.expires_in * 1000;
  
  localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, tokens.access_token);
  localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, tokens.refresh_token);
  localStorage.setItem(STORAGE_KEYS.EXPIRES_AT, expiresAt.toString());
  
  if (tokens.id_token) {
    localStorage.setItem(STORAGE_KEYS.ID_TOKEN, tokens.id_token);
  }
}

/**
 * Get stored access token
 */
export function getAccessToken(): string | null {
  return localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
}

/**
 * Get stored refresh token
 */
export function getRefreshToken(): string | null {
  return localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
}

/**
 * Check if token is expired (or will expire within a buffer window)
 */
export function isTokenExpired(bufferMs: number = 0): boolean {
  const expiresAt = localStorage.getItem(STORAGE_KEYS.EXPIRES_AT);
  
  if (!expiresAt) {
    return true;
  }
  
  return Date.now() >= parseInt(expiresAt, 10) - bufferMs;
}

let refreshInFlight: Promise<boolean> | null = null;

/**
 * Refresh access token
 *
 * Refresh tokens are single-use: the backend deletes the jti on rotation, so a
 * second request carrying the same token is rejected. Returning to an idle tab
 * fires the renewal timer, the visibility handler and every page fetch at once,
 * so concurrent callers share one in-flight rotation, and a Web Lock serialises
 * rotation across tabs (they all share the one refresh token in localStorage).
 */
export function refreshAccessToken(): Promise<boolean> {
  if (!refreshInFlight) {
    refreshInFlight = withRefreshLock(rotateTokens).finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

function withRefreshLock(fn: () => Promise<boolean>): Promise<boolean> {
  if (typeof navigator !== 'undefined' && navigator.locks) {
    return navigator.locks.request('admin-token-refresh', fn);
  }
  return fn();
}

async function rotateTokens(): Promise<boolean> {
  // Another tab may have rotated while we waited for the lock. Callers refresh
  // at most 2 minutes ahead of expiry, so anything fresher is already done.
  if (!isTokenExpired(120_000)) {
    return true;
  }

  const refreshToken = getRefreshToken();

  if (!refreshToken) {
    return false;
  }

  try {
    const response = await fetch(`${API_URL}/api/admin/auth/social/refresh`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });

    const data = await response.json();

    if (!data.success) {
      return false;
    }

    // Backend wraps response via successResponse(): { success, data: { access_token, ... } }
    const tokens = data.data || data;
    
    storeTokens({
      access_token: tokens.access_token,
      refresh_token: tokens.refresh_token,
      expires_in: tokens.expires_in,
    });
    
    return true;
  } catch (error) {
    console.error('Error refreshing token:', error);
    return false;
  }
}

/**
 * Get user info from token
 */
export function getUserInfo(): UserInfo | null {
  const userInfoStr = localStorage.getItem(STORAGE_KEYS.USER_INFO);
  
  if (!userInfoStr) {
    return null;
  }
  
  try {
    return JSON.parse(userInfoStr);
  } catch {
    return null;
  }
}

/**
 * Store user info
 */
export function storeUserInfo(userInfo: UserInfo): void {
  localStorage.setItem(STORAGE_KEYS.USER_INFO, JSON.stringify(userInfo));
}

/**
 * Decode JWT token to get user info
 */
export function decodeToken(token: string): UserInfo | null {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Error decoding token:', error);
    return null;
  }
}

/**
 * Check whether the current session belongs to an admin.
 *
 * Admin tokens carry an `isAdmin` claim, stamped by the backend on every issue
 * and rotation, so revoking admin in the DB takes effect on the next refresh.
 */
export function hasAdminRole(): boolean {
  const accessToken = getAccessToken();

  if (!accessToken) {
    return false;
  }

  try {
    const decoded: any = decodeToken(accessToken);
    return decoded?.isAdmin === true;
  } catch (error) {
    console.error('Error checking admin role:', error);
    return false;
  }
}

/**
 * Check if user is authenticated
 */
export function isAuthenticated(): boolean {
  const accessToken = getAccessToken();
  
  if (!accessToken) {
    return false;
  }
  
  if (isTokenExpired()) {
    // If we have a refresh token, consider still authenticated
    // (the proactive refresh or apiRequest will handle the actual refresh)
    return !!getRefreshToken();
  }
  
  return true;
}

/**
 * Logout user
 */
export async function logout(): Promise<void> {
  stopSessionRenewal();
  
  const refreshToken = getRefreshToken();
  if (refreshToken) {
    try {
      await fetch(`${API_URL}/api/admin/auth/social/logout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });
    } catch (error) {
      console.error('Error during logout:', error);
    }
  }

  // Clear all stored data
  localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
  localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
  localStorage.removeItem(STORAGE_KEYS.EXPIRES_AT);
  localStorage.removeItem(STORAGE_KEYS.ID_TOKEN);
  localStorage.removeItem(STORAGE_KEYS.USER_INFO);
}

/**
 * Make authenticated API request
 *
 * The backend wraps all responses via successResponse() which produces:
 *   { success, requestId, data: <payload>, timestamp }
 *
 * This function unwraps the envelope so callers receive:
 *   { success, ...payload }
 *
 * For example, if the backend sends successResponse(res, { data: users, pagination }),
 * the raw response is { success: true, data: { data: users, pagination } }.
 * After unwrapping, callers get { success: true, data: users, pagination }.
 */
export async function apiRequest<T = any>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  let accessToken = getAccessToken();
  
  // Refresh token if expired
  if (isTokenExpired()) {
    const refreshed = await refreshAccessToken();
    
    if (!refreshed) {
      throw new Error('Session expired. Please login again.');
    }
    
    accessToken = getAccessToken();
  }
  
  const response = await fetch(`${API_URL}${endpoint}`, {
    ...options,
    headers: {
      ...options.headers,
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
  });
  
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const msg =
      errorData.message ||
      (typeof errorData.error === 'string' ? errorData.error : errorData.error?.message) ||
      `API request failed: ${response.statusText}`;
    throw new Error(msg);
  }
  
  const json = await response.json();

  // Unwrap the standardized API envelope: { success, data: <payload>, ... }
  // Spread the inner payload so callers can access fields directly
  // Only unwrap when data is a plain object (not an array)
  if (json && typeof json === 'object' && 'success' in json && 'data' in json && typeof json.data === 'object' && json.data !== null && !Array.isArray(json.data)) {
    return { success: json.success, ...json.data } as T;
  }

  return json;
}

/** Download a bounded server-generated CSV using the current admin session. */
export async function apiDownload(endpoint: string, filename: string): Promise<void> {
  let accessToken = getAccessToken();
  if (isTokenExpired()) {
    const refreshed = await refreshAccessToken();
    if (!refreshed) throw new Error('Session expired. Please login again.');
    accessToken = getAccessToken();
  }
  const response = await fetch(`${API_URL}${endpoint}`, { headers: { Authorization: `Bearer ${accessToken}` } });
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body.message || body.error?.message || 'CSV export failed. Narrow the report filters and try again.');
  }
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url; anchor.download = filename; document.body.appendChild(anchor); anchor.click(); anchor.remove(); URL.revokeObjectURL(url);
}

/**
 * Make authenticated API request with FormData (multipart/form-data).
 * Does NOT set Content-Type header — the browser sets it automatically with the boundary.
 */
export async function apiUpload<T = any>(
  endpoint: string,
  formData: FormData,
  timeout: number = 180000
): Promise<T> {
  let accessToken = getAccessToken();

  if (isTokenExpired()) {
    const refreshed = await refreshAccessToken();
    if (!refreshed) {
      throw new Error('Session expired. Please login again.');
    }
    accessToken = getAccessToken();
  }

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  let response: Response;
  try {
    response = await fetch(`${API_URL}${endpoint}`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
      },
      body: formData,
      signal: controller.signal,
    });
  } catch (error) {
    clearTimeout(timeoutId);
    if (error instanceof DOMException && error.name === 'AbortError') {
      throw new Error(`Request timed out after ${timeout / 1000}s. Please try again.`);
    }
    throw error;
  }

  clearTimeout(timeoutId);

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const msg =
      errorData.message ||
      (typeof errorData.error === 'string' ? errorData.error : errorData.error?.message) ||
      `API request failed: ${response.statusText}`;
    throw new Error(msg);
  }

  const json = await response.json();

  if (json && typeof json === 'object' && 'success' in json && 'data' in json && typeof json.data === 'object' && json.data !== null && !Array.isArray(json.data)) {
    return { success: json.success, ...json.data } as T;
  }

  return json;
}


// ── Proactive Session Renewal ──────────────────────────────────────────

let refreshTimer: ReturnType<typeof setTimeout> | null = null;

/**
 * Schedule a token refresh before it expires.
 * Refreshes 60 seconds before expiry (or immediately if already close).
 */
function scheduleTokenRefresh(): void {
  clearScheduledRefresh();

  const expiresAt = localStorage.getItem(STORAGE_KEYS.EXPIRES_AT);
  if (!expiresAt) return;

  const expiresAtMs = parseInt(expiresAt, 10);
  // Refresh 60s before expiry
  const refreshAt = expiresAtMs - 60_000;
  const delay = Math.max(refreshAt - Date.now(), 0);

  refreshTimer = setTimeout(async () => {
    const ok = await refreshAccessToken();
    if (ok) {
      scheduleTokenRefresh(); // schedule next refresh
    } else {
      // Refresh failed — redirect to login
      window.location.href = '/login';
    }
  }, delay);
}

function clearScheduledRefresh(): void {
  if (refreshTimer !== null) {
    clearTimeout(refreshTimer);
    refreshTimer = null;
  }
}

/**
 * Call once on app startup (e.g. after login callback stores tokens).
 * Sets up proactive refresh timer + visibility-change handler.
 */
export function startSessionRenewal(): void {
  scheduleTokenRefresh();

  // When user returns to the tab, check & refresh if needed
  document.addEventListener('visibilitychange', handleVisibilityChange);
}

export function stopSessionRenewal(): void {
  clearScheduledRefresh();
  document.removeEventListener('visibilitychange', handleVisibilityChange);
}

async function handleVisibilityChange(): Promise<void> {
  if (document.visibilityState !== 'visible') return;

  // If token is expired or will expire within 2 minutes, refresh now
  if (isTokenExpired(120_000)) {
    const ok = await refreshAccessToken();
    if (ok) {
      scheduleTokenRefresh();
    } else {
      window.location.href = '/login';
    }
  }
}
